import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const createIcon = (color) => new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    background-color: ${color};
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

const greenIcon = createIcon('#22c55e');
const orangeIcon = createIcon('#f97316');
const blueIcon = createIcon('#3b82f6');
const redIcon = createIcon('#ef4444');

// Component to fit bounds
const FitBounds = ({ schools }) => {
  const map = useMap();
  
  useEffect(() => {
    if (schools.length > 0) {
      const validSchools = schools.filter(s => s.latitude && s.longitude);
      if (validSchools.length > 0) {
        const bounds = L.latLngBounds(validSchools.map(s => [s.latitude, s.longitude]));
        map.fitBounds(bounds, { padding: [20, 20] });
      }
    }
  }, [schools, map]);
  
  return null;
};

const SchoolMap = ({ token, language = 'hi', onSchoolSelect, filters = {} }) => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, withContact: 0, whatsapp: 0, phone: 0, principal: 0, basic: 0 });
  const [colorFilter, setColorFilter] = useState('all'); // 'all', 'whatsapp', 'phone', 'principal', 'basic'
  const [mapFilters, setMapFilters] = useState({
    state: filters.state || 'MADHYA PRADESH',
    hasLatLong: true,
    limit: 3000  // Load more schools for map
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadSchools();
  }, [mapFilters]);

  const loadSchools = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        state: mapFilters.state,
        limit: mapFilters.limit,
        has_latlong: 'yes'
      });
      
      const res = await fetch(`${API_URL}/api/school-finder/search?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.schools) {
        const schoolsWithLocation = data.schools.filter(s => s.latitude && s.longitude);
        setSchools(schoolsWithLocation);
        
        // Calculate stats by category
        const whatsappCount = schoolsWithLocation.filter(s => s.whatsapp_number).length;
        const phoneCount = schoolsWithLocation.filter(s => !s.whatsapp_number && (s.mobile_number || s.phone)).length;
        const principalCount = schoolsWithLocation.filter(s => !s.whatsapp_number && !s.mobile_number && !s.phone && s.principal_name).length;
        const basicCount = schoolsWithLocation.filter(s => !s.whatsapp_number && !s.mobile_number && !s.phone && !s.principal_name).length;
        
        setStats({
          total: schoolsWithLocation.length,
          withContact: whatsappCount + phoneCount,
          whatsapp: whatsappCount,
          phone: phoneCount,
          principal: principalCount,
          basic: basicCount
        });
      }
    } catch (err) {
      console.error('Failed to load schools:', err);
    }
    setLoading(false);
  };

  // Get school category for filtering
  const getSchoolCategory = (school) => {
    if (school.whatsapp_number) return 'whatsapp';
    if (school.mobile_number || school.phone) return 'phone';
    if (school.principal_name) return 'principal';
    return 'basic';
  };

  // Filter schools by color/category
  const filteredSchools = colorFilter === 'all' 
    ? schools 
    : schools.filter(s => getSchoolCategory(s) === colorFilter);

  const getMarkerColor = (school) => {
    if (school.whatsapp_number) return '#22c55e';
    if (school.mobile_number || school.phone) return '#3b82f6';
    if (school.principal_name) return '#f97316';
    return '#ef4444';
  };

  // India center coordinates
  const indiaCenter = [22.9734, 78.6569];
  const mpCenter = [23.2599, 77.4126];

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="school-map-container">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-500 p-4 text-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2">
              🗺️ {language === 'hi' ? 'स्कूल मैप (Master Data)' : 'School Map (Master Data)'}
            </h3>
            <p className="text-sm text-teal-100">
              {loading ? '⏳ Loading...' : `${stats.total.toLocaleString()} schools with location`}
            </p>
          </div>
          
          {/* Filters */}
          <div className="flex gap-2 items-center">
            <select
              value={mapFilters.state}
              onChange={(e) => setMapFilters(prev => ({ ...prev, state: e.target.value }))}
              className="px-3 py-1.5 rounded text-gray-800 text-sm"
              data-testid="map-state-filter"
            >
              <option value="MADHYA PRADESH">MADHYA PRADESH</option>
              <option value="UTTAR PRADESH">UTTAR PRADESH</option>
              <option value="RAJASTHAN">RAJASTHAN</option>
              <option value="MAHARASHTRA">MAHARASHTRA</option>
              <option value="BIHAR">BIHAR</option>
              <option value="GUJARAT">GUJARAT</option>
              <option value="KARNATAKA">KARNATAKA</option>
              <option value="TAMIL NADU">TAMIL NADU</option>
              <option value="ANDHRA PRADESH">ANDHRA PRADESH</option>
              <option value="TELANGANA">TELANGANA</option>
              <option value="CHHATTISGARH">CHHATTISGARH</option>
            </select>
            
            <button
              onClick={loadSchools}
              disabled={loading}
              className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded text-sm"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
        
        {/* Legend - Clickable Filters */}
        <div className="flex gap-2 mt-3 text-xs flex-wrap">
          <button 
            onClick={() => setColorFilter('all')}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${colorFilter === 'all' ? 'bg-white text-gray-800 shadow' : 'bg-white/20 hover:bg-white/30'}`}
            data-testid="filter-all"
          >
            📊 All ({stats.total.toLocaleString()})
          </button>
          <button 
            onClick={() => setColorFilter('whatsapp')}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${colorFilter === 'whatsapp' ? 'bg-green-400 text-white shadow' : 'bg-white/20 hover:bg-white/30'}`}
            data-testid="map-filter-whatsapp"
          >
            <span className="w-3 h-3 rounded-full bg-green-500 border border-white"></span>
            WhatsApp ({stats.whatsapp})
          </button>
          <button 
            onClick={() => setColorFilter('phone')}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${colorFilter === 'phone' ? 'bg-blue-400 text-white shadow' : 'bg-white/20 hover:bg-white/30'}`}
            data-testid="map-filter-phone"
          >
            <span className="w-3 h-3 rounded-full bg-blue-500 border border-white"></span>
            Phone ({stats.phone})
          </button>
          <button 
            onClick={() => setColorFilter('principal')}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${colorFilter === 'principal' ? 'bg-orange-400 text-white shadow' : 'bg-white/20 hover:bg-white/30'}`}
            data-testid="map-filter-principal"
          >
            <span className="w-3 h-3 rounded-full bg-orange-500 border border-white"></span>
            Principal ({stats.principal})
          </button>
          <button 
            onClick={() => setColorFilter('basic')}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${colorFilter === 'basic' ? 'bg-red-400 text-white shadow' : 'bg-white/20 hover:bg-white/30'}`}
            data-testid="map-filter-basic"
          >
            <span className="w-3 h-3 rounded-full bg-red-500 border border-white"></span>
            Basic ({stats.basic})
          </button>
        </div>
      </div>
      
      {/* Map */}
      <div style={{ height: '500px', width: '100%' }}>
        <MapContainer
          center={mpCenter}
          zoom={7}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <FitBounds schools={filteredSchools} />
          
          {filteredSchools.map((school, idx) => (
            <CircleMarker
              key={school.id || idx}
              center={[school.latitude, school.longitude]}
              radius={6}
              pathOptions={{
                color: 'white',
                weight: 2,
                fillColor: getMarkerColor(school),
                fillOpacity: 0.9
              }}
            >
              <Popup>
                <div className="min-w-[220px] max-w-[280px]">
                  <h4 className="font-bold text-sm text-gray-800 mb-2 leading-tight">
                    {school.school_name?.substring(0, 50)}
                  </h4>
                  <p className="text-xs text-gray-600 mb-2">
                    📍 {school.district || '-'}, {school.block || '-'}
                  </p>
                  
                  {school.principal_name && (
                    <p className="text-xs mb-1">
                      👤 <strong>Principal:</strong> {school.principal_name}
                    </p>
                  )}
                  
                  {school.total_students && (
                    <p className="text-xs mb-1">
                      👥 <strong>Students:</strong> {school.total_students}
                    </p>
                  )}
                  
                  {(school.mobile_number || school.phone) && (
                    <p className="text-xs mb-1">
                      📞 <strong>Phone:</strong> {school.mobile_number || school.phone}
                    </p>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex gap-1 mt-3 flex-wrap">
                    {school.whatsapp_number && (
                      <a 
                        href={`https://wa.me/91${school.whatsapp_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 px-2 py-1.5 bg-green-500 text-white text-xs rounded hover:bg-green-600 text-center"
                      >
                        📱 WhatsApp
                      </a>
                    )}
                    
                    {(school.mobile_number || school.phone) && (
                      <a 
                        href={`tel:${school.mobile_number || school.phone}`}
                        className="flex-1 px-2 py-1.5 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 text-center"
                      >
                        📞 Call
                      </a>
                    )}
                  </div>
                  
                  <button
                    onClick={(e) => { e.stopPropagation(); onSchoolSelect && onSchoolSelect(school); }}
                    className="block mt-2 w-full px-2 py-1.5 bg-orange-500 text-white text-xs rounded hover:bg-orange-600 text-center font-medium"
                  >
                    📋 {language === 'hi' ? 'पूरी जानकारी देखें' : 'View Full Details'}
                  </button>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
      
      {/* Footer Stats */}
      <div className="bg-gray-50 p-3 border-t flex justify-between items-center text-sm">
        <div className="flex gap-4">
          <span className="text-gray-600">
            📊 <strong>{filteredSchools.length}</strong> {colorFilter !== 'all' ? `(${colorFilter})` : ''} schools showing
          </span>
          {colorFilter === 'all' && (
            <span className="text-green-600">
              📞 <strong>{stats.withContact}</strong> with contact
            </span>
          )}
        </div>
        <a
          href={`${API_URL}/api/school-finder/export-excel?state=${mapFilters.state}&has_latlong=yes&limit=10000`}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
          data-testid="map-export-btn"
        >
          📥 Excel Export
        </a>
      </div>
    </div>
  );
};

export default SchoolMap;
